/**
 * The time window the device keeps synced for the tables that grow one row
 * per day forever.
 *
 * The window is expressed as a list of 'YYYY-MM' keys rather than a boundary
 * date, because that is the only shape the sync rules can act on: the rules
 * engine has no clock, and it refuses to compare row data against a
 * subscription parameter with anything but equality. The sync config keys each
 * month into its own bucket (see `rate_calendar` in
 * `powersync/sync-config.yaml`), so a coarse month quantum is also what keeps
 * rolling the window cheap — one bucket joins, one leaves, the rest are
 * untouched.
 */

/** Months of `rate_calendar` kept behind the current one. */
const MONTHS_BEHIND = 1;

/**
 * Months of `rate_calendar` kept ahead of the current one. Generous on
 * purpose: rates are priced forward, and a night that falls outside the
 * window is absent rather than stale, so the cost of being wrong is a blank
 * price cell. The unbounded growth this fixes is all in the past — nothing
 * reads a night once it has gone by.
 */
const MONTHS_AHEAD = 12;

/** UTC calendar month ('YYYY-MM') of an instant. */
export function isoMonth(date: Date): string {
  return date.toISOString().slice(0, 7);
}

/**
 * The 'YYYY-MM' keys the client subscribes `rate_calendar` for, oldest first.
 *
 * Quantized to the month and computed in UTC, so every launch inside the same
 * month produces the identical list — which is what stops the subscription
 * minting a fresh set of buckets (and re-downloading the table) on each start.
 * The month a night belongs to is the property's local one and this is the
 * device's UTC one, but the two can only disagree by a day at a boundary, and
 * a whole month of slack on either side absorbs that.
 */
export function rateCalendarMonths(now: Date = new Date()): string[] {
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth();
  const months: string[] = [];
  for (let offset = -MONTHS_BEHIND; offset <= MONTHS_AHEAD; offset += 1) {
    months.push(isoMonth(new Date(Date.UTC(year, month + offset, 1))));
  }
  return months;
}
